import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchZones, createZone, updateZone, deleteZone } from "../api/api";

const EMPTY_FORM = { zone_number: "", name: "" };

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function Zones() {

  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zonesError, setZonesError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [rowBusy, setRowBusy] = useState(null);
  const [rowError, setRowError] = useState("");

  const loadZones = async () => {
    setZonesLoading(true);
    setZonesError("");
    try {
      const data = await fetchZones();
      setZones(data);
    } catch (err) {
      setZonesError(errorMessage(err));
    } finally {
      setZonesLoading(false);
    }
  };

  useEffect(() => {
    loadZones();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.zone_number.trim()) {
      setFormError("Zone Number is required.");
      return;
    }

    setSubmitting(true);
    try {
      await createZone({
        zone_number: form.zone_number.trim(),
        name: form.name.trim() || undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadZones();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (zone) => {
    setEditingId(zone.id);
    setEditForm({ zone_number: zone.zone_number, name: zone.name || "" });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async (zoneId) => {
    if (!editForm.zone_number.trim()) {
      setRowError("Zone Number is required.");
      return;
    }

    setRowBusy(zoneId);
    setRowError("");
    try {
      const updated = await updateZone(zoneId, {
        zone_number: editForm.zone_number.trim(),
        name: editForm.name.trim() || null,
      });
      setZones((prev) => prev.map((z) => (z.id === zoneId ? updated : z)));
      setEditingId(null);
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.zone_number}"? Plots currently assigned to it will need a new zone.`)) {
      return;
    }

    setRowBusy(zone.id);
    setRowError("");
    try {
      await deleteZone(zone.id);
      setZones((prev) => prev.filter((z) => z.id !== zone.id));
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: "0", marginBottom: 0 }}>Zones</h1>
          <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? "Cancel" : "+ Add Zone"}
          </button>
        </div>

        {zonesError && <p className="login-error" style={{ color: "red" }}>{zonesError}</p>}
        {rowError && <p className="login-error" style={{ color: "red" }}>{rowError}</p>}

        {showForm && (
          <div className="section-card">
            <h3>New Zone</h3>

            {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}

            <form onSubmit={handleCreateZone}>
              <input
                type="text"
                name="zone_number"
                placeholder="Zone Number *"
                value={form.zone_number}
                onChange={handleFormChange}
                required
              />
              <input
                type="text"
                name="name"
                placeholder="Zone Name (optional)"
                value={form.name}
                onChange={handleFormChange}
              />

              <button type="submit" className="login-btn" style={{ width: "auto", padding: "8px 16px" }} disabled={submitting}>
                {submitting ? "Creating…" : "Create Zone"}
              </button>
            </form>
          </div>
        )}

        {zonesLoading && <p>Loading zones…</p>}

        {!zonesLoading && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>

              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Zone Number</th>
                  <th>Name</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {zones.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: "15px" }}>No zones yet.</td></tr>
                )}

                {zones.map((zone) => (
                  <tr key={zone.id}>
                    {editingId === zone.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            name="zone_number"
                            value={editForm.zone_number}
                            onChange={handleEditFormChange}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditFormChange}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>{new Date(zone.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              title="Save"
                              onClick={() => saveEdit(zone.id)}
                              disabled={rowBusy === zone.id}
                              style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={cancelEdit}
                              disabled={rowBusy === zone.id}
                              style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td><b>{zone.zone_number}</b></td>
                        <td>{zone.name || "—"}</td>
                        <td>{new Date(zone.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button className="view-btn" onClick={() => startEdit(zone)} disabled={rowBusy === zone.id}>
                              Edit
                            </button>
                            <button
                              className="view-btn"
                              onClick={() => handleDelete(zone)}
                              disabled={rowBusy === zone.id}
                              style={{ background: "#dc2626", color: "white" }}
                            >
                              {rowBusy === zone.id ? "…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

      </div>

    </div>
  );
}

export default Zones;
